import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Remarkable } from 'remarkable'
import moment from 'moment-wrapper'
import styled from 'styled-components'

import * as sessionUserSelector from 'selectors/sessionUser'

import colors from 'components/utils/colors'
import Badge from 'components/Shared/Badge/Index'

const md = new Remarkable()

const StyledReleaseComponent = styled.div`
  code {
    color: #666;
    margin: 0;
    padding-left: 3em;
    border-left: 0.5em #eee solid;
  }
  padding-top: 8px;
  padding-bottom: 20px;
`

const ReleaseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`

const TitleText = styled.div`
  font-family: Exo;
  font-size: 28px;
  font-weight: 300;
  line-height: 37px;
  color: ${colors.secondary20};
`

const StyledReleaseDescription = styled.div`
  margin-top: 24px;
  max-width: 736px;
  cursor: default;
  div {
    color: ${colors.secondary20};
    font-style: normal;
    line-height: 24px;
    overflow-wrap: break-word;

    table {
      border-collapse: collapse;
      border-spacing: 0;
      border: 1px solid ${colors.secondary95};
    }
    thead {
      border-bottom: 1px solid ${colors.secondary95};
      font-weight: 600;
      background-color: ${colors.secondary95};
    }
    th {
      font-weight: 600;
    }
    th, td {
      padding: 6px 13px;
    }
    tbody {
      tr {
        &:nth-child(odd) {
          background-color: #FFFFFF;
        }

        &:nth-child(even) {
          background-color: ${colors.secondary95};
        }
      }
    }
    h2, h3 {
      font-family: Exo;
    }
    h2 {
      &:nth-of-type(1) {
        padding-bottom: 8px;
        border-bottom: 2px solid ${colors.secondary90};
      }
      font-size: 20px;
      line-height: 26px;
      font-weight: 400;
    }

    h2:nth-of-type(2) {
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
      padding: 8px 16px 12px 16px;
    }
    h3 {
      padding: 8px 16px;
      line-height: 18px;
      font-size: 14px;
      font-weight: 500;
    }
    p {
      margin-top: 0;
      margin-bottom: 0;
      padding-bottom: 2px;
      font-size: 14px;
      line-height: 24px;
      white-space: pre;
    }
    ul {
      padding: 0px 16px 12px 42px;
      list-style: inherit;
      font-size: 14px;
      line-height: 24px;
      border-bottom: 2px solid ${colors.secondary90};
    }
    em {
      font-weight: 600;
    }
  }
`

const StyledEmpty = styled.label`
`

const ReleaseNote = ({ releaseNote }) => {
  const isAdmin = useSelector(sessionUserSelector.isAdmin)

  const { t } = useTranslation()

  if (!releaseNote) {
    return null
  }

  const canView = releaseNote.published || isAdmin

  if (!canView) {
    return (
      <StyledEmpty>
        {t('releaseNotesPage.releaseEmpty')}
      </StyledEmpty>
    )
  }

  const renderMarkdown = (source) => <div dangerouslySetInnerHTML={{ __html: md.render(source) }} />
  const date = moment(releaseNote.date).tz('UTC').format('MMMM DD, YYYY')
  const title = `${releaseNote.version} (${date})`
  const badgeText = releaseNote.published ? t('releaseNotesPage.publishedBadge') : t('releaseNotesPage.unpublishedBadge')
  const badgeColor = releaseNote.published ? colors.success63 : colors.warning80

  return (
    <StyledReleaseComponent>
      <ReleaseHeader data-test='release-header'>
        <TitleText data-test='release-title'>{title}</TitleText>
        {isAdmin && (
          <Badge
            big
            color={badgeColor}
            margin='0'
          >
            {badgeText}
          </Badge>
        )}
      </ReleaseHeader>
      <StyledReleaseDescription data-test='release-description'>
        {renderMarkdown(releaseNote.description)}
      </StyledReleaseDescription>
    </StyledReleaseComponent>
  )
}

ReleaseNote.propTypes = {
  releaseNote: PropTypes.object,
}

export default ReleaseNote
